/**
 * A ranked row opened up: how its score is made — the hour optimizer's
 * breakdown table, fed the formula that was scored (blocks read as math, the
 * points add up to the score) — and every value it has, colored by family,
 * one tab per range when the formula reads several. An unranked row says why
 * instead of a score.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode, useState } from 'react';
import { type DropGenHourFormula, type DropGenHourScoreBreakdownRow, hourRangeKey, hourRangeLabel } from '@trafficpulse/shared';

import { SegmentedPills } from '@/components/ui';

import { HourScoreBreakdown } from '../../../general/hour/HourScoreBreakdown';
import { HourAllValues } from '../../../general/hour/values/HourAllValues';

interface Props {
  formula: DropGenHourFormula;
  breakdown?: DropGenHourScoreBreakdownRow[];
  metricsByRange: Record<string, Record<string, number>>;
  /** Why the row has no score (an exclude rule's words), when it has none. */
  unrankedNote?: string | null;
  /** One closing line (e.g. how a prompt group is scored). */
  footnote?: string;
}

const Section: FC<{ title: string; note?: string; right?: ReactNode; children: ReactNode }> = ({ title, note, right, children }) => (
  <div className="flex flex-col gap-2">
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
      {note ? <span className="text-[11px] text-muted-foreground">{note}</span> : null}
      {right ? <div className="ml-auto">{right}</div> : null}
    </div>
    {children}
  </div>
);

export const ScoreDetail: FC<Props> = ({ formula, breakdown, metricsByRange, unrankedNote, footnote }) => {
  const ranges = [...new Set(formula.groups.map(hourRangeKey))];
  const [range, setRange] = useState(ranges[0]);
  const bag = metricsByRange[range];
  return (
    <div className="flex flex-col gap-4 border-t border-border/60 bg-muted/20 px-4 py-3">
      {breakdown && breakdown.length > 0 ? (
        <Section title="How the score is made" note="each range's formula on its own values, times its share">
          <HourScoreBreakdown rows={breakdown} formula={formula} />
        </Section>
      ) : (
        <p className="text-xs text-muted-foreground">{unrankedNote ?? 'Not ranked — no score to explain.'}</p>
      )}
      <Section
        title="All values"
        note={ranges.length > 1 ? undefined : hourRangeLabel(range)}
        right={
          ranges.length > 1 ? (
            <SegmentedPills<string> options={ranges.map((k) => ({ value: k, label: hourRangeLabel(k) }))} value={range} onChange={setRange} />
          ) : null
        }
      >
        {bag ? <HourAllValues bag={bag} /> : <p className="text-xs text-muted-foreground">No row in {hourRangeLabel(range)} — nothing was sent or clicked there.</p>}
      </Section>
      {footnote ? <p className="text-[11px] text-muted-foreground">{footnote}</p> : null}
    </div>
  );
};
